import {
  Direction, SecurityTypes, Symbol, CurrencyTypes, OrderRecord, symbolDetails, SymbolLookupRequest,
  FuturesSnapshotPricing,
  LastTrade,
} from '@qep/types/src/index';
import {isUndefined} from 'lodash';
import {isTestingQueryEnabled} from '../utils/runtime-config';

const symbolsCache = new Map<string, Symbol>();

/**
 * Enables deterministic market-data fallback symbols only for whitelisted e2e runtime environments.
 */
const isTestingSymbolFallbackEnabled = () => isTestingQueryEnabled(globalThis.location?.search ?? '');

/**
 * Builds deterministic futures symbols for e2e lookups when market data is unavailable.
 */
const buildTestingFallbackSymbols = (searchTerm: string): Symbol[] => {
  const normalizedSearch = searchTerm.trim().toUpperCase();
  const baseSymbols = [
    {
      symbol: 'ESH6',
      exchangeSymbol: 'ESH6',
      normalizedSymbol: 'ESH6',
      normalizedExchangeSymbol: 'ESH6',
      normalizedSymbolWithMic: 'ESH6_XCME',
      rootSymbol: 'ES',
      segmentMic: 'XCME',
      description: 'E-mini S&P 500 Mar26',
      name: 'E-mini S&P 500 Mar26',
      secType: SecurityTypes.Future,
      contractSize: '50.0000000000',
      contractUnits: 'USD per point',
      tickSize: '0.2500000000',
      strategy: 'FUT',
      currency: 'USD',
      expiry: '2026-03-20T00:00:00.000Z',
      contractYear: 2026,
      contractMonth: 3,
      contractYYYYMM: '202603',
      strikePrice: null,
      callPut: null,
      firstNoticeDate: '2026-03-20T00:00:00.000Z',
      decimalPlaces: 2,
      joinField: 'parent_doc',
      underlyingRootSymbol: null,
      isBoosted: false,
      isTradeble: true,
    },
    {
      symbol: 'NQH6',
      exchangeSymbol: 'NQH6',
      normalizedSymbol: 'NQH6',
      normalizedExchangeSymbol: 'NQH6',
      normalizedSymbolWithMic: 'NQH6_XCME',
      rootSymbol: 'NQ',
      segmentMic: 'XCME',
      description: 'E-mini Nasdaq-100 Mar26',
      name: 'E-mini Nasdaq-100 Mar26',
      secType: SecurityTypes.Future,
      contractSize: '20.0000000000',
      contractUnits: 'USD per point',
      tickSize: '0.2500000000',
      strategy: 'FUT',
      currency: 'USD',
      expiry: '2026-03-20T00:00:00.000Z',
      contractYear: 2026,
      contractMonth: 3,
      contractYYYYMM: '202603',
      strikePrice: null,
      callPut: null,
      firstNoticeDate: '2026-03-20T00:00:00.000Z',
      decimalPlaces: 2,
      joinField: 'parent_doc',
      underlyingRootSymbol: null,
      isBoosted: false,
      isTradeble: true,
    },
    {
      symbol: 'ESM6',
      exchangeSymbol: 'ESM6',
      normalizedSymbol: 'ESM6',
      normalizedExchangeSymbol: 'ESM6',
      normalizedSymbolWithMic: 'ESM6_XCME',
      rootSymbol: 'ES',
      segmentMic: 'XCME',
      description: 'E-mini S&P 500 Jun26',
      name: 'E-mini S&P 500 Jun26',
      secType: SecurityTypes.Future,
      contractSize: '50.0000000000',
      contractUnits: 'USD per point',
      tickSize: '0.2500000000',
      strategy: 'FUT',
      currency: 'USD',
      expiry: '2026-06-19T00:00:00.000Z',
      contractYear: 2026,
      contractMonth: 6,
      contractYYYYMM: '202606',
      strikePrice: null,
      callPut: null,
      firstNoticeDate: '2026-06-19T00:00:00.000Z',
      decimalPlaces: 2,
      joinField: 'parent_doc',
      underlyingRootSymbol: null,
      isBoosted: false,
      isTradeble: true,
    },
    {
      symbol: 'ESU6',
      exchangeSymbol: 'ESU6',
      normalizedSymbol: 'ESU6',
      normalizedExchangeSymbol: 'ESU6',
      normalizedSymbolWithMic: 'ESU6_XCME',
      rootSymbol: 'ES',
      segmentMic: 'XCME',
      description: 'E-mini S&P 500 Sep26',
      name: 'E-mini S&P 500 Sep26',
      secType: SecurityTypes.Future,
      contractSize: '50.0000000000',
      contractUnits: 'USD per point',
      tickSize: '0.2500000000',
      strategy: 'FUT',
      currency: 'USD',
      expiry: '2026-09-18T00:00:00.000Z',
      contractYear: 2026,
      contractMonth: 9,
      contractYYYYMM: '202609',
      strikePrice: null,
      callPut: null,
      firstNoticeDate: '2026-09-18T00:00:00.000Z',
      decimalPlaces: 2,
      joinField: 'parent_doc',
      underlyingRootSymbol: null,
      isBoosted: false,
      isTradeble: true,
    },
    {
      symbol: 'CLH6',
      exchangeSymbol: 'CLH6',
      normalizedSymbol: 'CLH6',
      normalizedExchangeSymbol: 'CLH6',
      normalizedSymbolWithMic: 'CLH6_XNYM',
      rootSymbol: 'CL',
      segmentMic: 'XNYM',
      description: 'Crude Oil Mar26',
      name: 'Crude Oil Mar26',
      secType: SecurityTypes.Future,
      contractSize: '1000.0000000000',
      contractUnits: 'USD per point',
      tickSize: '0.0100000000',
      strategy: 'FUT',
      currency: 'USD',
      expiry: '2026-02-20T00:00:00.000Z',
      contractYear: 2026,
      contractMonth: 3,
      contractYYYYMM: '202603',
      strikePrice: null,
      callPut: null,
      firstNoticeDate: '2026-02-20T00:00:00.000Z',
      decimalPlaces: 2,
      joinField: 'parent_doc',
      underlyingRootSymbol: null,
      isBoosted: false,
      isTradeble: true,
    },
  ] as Symbol[];

  if (!normalizedSearch) {
    return baseSymbols;
  }

  return baseSymbols.filter(symbol =>
    symbol.symbol?.toUpperCase().includes(normalizedSearch)
    || symbol.exchangeSymbol?.toUpperCase().includes(normalizedSearch)
    || symbol.rootSymbol?.toUpperCase().includes(normalizedSearch)
    || symbol.name?.toUpperCase().includes(normalizedSearch));
};

/**
 * Looks up symbols through QEP market data, caches successful results, and applies the e2e-only fallback.
 */
export async function symbolLookup(parameters: Omit<SymbolLookupRequest, 'symbol'> & {symbol: string}): Promise<Symbol[]> {
  const {symbol} = parameters;
  try {
    const symbols = await globalThis.QEP.apiServices.marketdata.symbolLookup({
      ...parameters,
      symbol: symbol.toUpperCase(),
    });
    const normalizedSymbols = symbols;
    if (normalizedSymbols.length === 0 && isTestingSymbolFallbackEnabled()) {
      return buildTestingFallbackSymbols(symbol);
    }

    for (const sym of normalizedSymbols) {
      symbolsCache.set(generateSymbolUniqueID(sym), sym);
    }

    return normalizedSymbols;
  } catch (error) {
    if (isTestingSymbolFallbackEnabled() && !(error instanceof DOMException && error.name === 'AbortError')) {
      return buildTestingFallbackSymbols(symbol);
    }

    throw error;
  }
}

type PricingSnapshot = {snapshot?: FuturesSnapshotPricing};

const getPrice = (pricing?: PricingSnapshot | null) => {
  if (pricing?.snapshot?.lastTrade?.price && pricing.snapshot?.lastTrade?.price !== '-') {
    return pricing?.snapshot?.lastTrade?.price;
  }

  const askPrice = pricing?.snapshot?.ask?.price;
  const bidPrice = pricing?.snapshot?.bid?.price;
  if (askPrice && bidPrice) {
    return getMidPrice(pricing);
  }

  return pricing?.snapshot?.previousDailySettlement?.settlementPrice;
};

const getMidPrice = (pricing?: PricingSnapshot | null) => {
  const priceDiff = pricing?.snapshot?.ask?.price - pricing?.snapshot?.bid?.price;
  return pricing?.snapshot?.ask?.price - (priceDiff / 2);
};

export function getSymbol(id: string): Symbol {
  if (symbolsCache.has(id)) {
    return symbolsCache.get(id)!;
  }

  throw new Error(`No symbol found for id ${id}`);
}

export function addSymbols(symbols: Symbol[]) {
  for (const sym of symbols) {
    symbolsCache.set(generateSymbolUniqueID(sym), sym);
  }
}

export function generateSymbolUniqueID({symbol, segmentMic}: Symbol) {
  return `${symbol} ${segmentMic}`;
}

export function calculateSymbolNotional(symbolDetails: OrderRecord, gross = false): number | null {
  const contractUnits = Number(symbolDetails?.symbol?.contractSize);
  const price = Number(getPrice(symbolDetails?.prices as {snapshot: FuturesSnapshotPricing}));
  const multiplier = (() => {
    switch (symbolDetails?.direction) {
      case Direction.Buy: {return 1;
      }

      case Direction.Sell: {return -1;
      }

      default: {return Number.NaN;
      }
    }
  })();
  const quantity = Number(symbolDetails?.quantity);

  if (Number.isNaN(quantity)
    || Number.isNaN(contractUnits)
    || Number.isNaN(price)
    || Number.isNaN(multiplier)
  ) {
    return null;
  }

  let value = Number(quantity * contractUnits * price * multiplier);
  if (gross) {
    value = Math.abs(value);
  }

  if (symbolDetails?.symbol?.currency === CurrencyTypes.cents) {
    value /= 100;
  }

  return value;
}

export const calculateSymbolNotionaNew = (symbolDetails: symbolDetails, symbolPrice?: LastTrade | null, gross = false): number | null => {
  if (symbolDetails) {
    let contractUnits: number | null = null;
    let price: number | null = null;
    let multiplier: 1 | -1 | null = null;
    let quantity: number | null = null;

    if (symbolPrice) {
      if (symbolDetails.symbol) {
        contractUnits = Number(symbolDetails.symbol.contractSize);
      }

      if (symbolPrice.price) {
        price = Number(symbolPrice.price);
      }
    }

    if (symbolDetails.direction) {
      multiplier = symbolDetails.direction === Direction.Buy ? 1 : -1;
    }

    if (symbolDetails.quantity) {
      quantity = Number(symbolDetails.quantity);
    }

    let value: number | null = null;
    if (quantity && contractUnits && price && multiplier) {
      value = Number(quantity * contractUnits * price * multiplier);
      if (gross) {
        value = Math.abs(value);
      }

      if (symbolDetails.symbol.currency === CurrencyTypes.cents) {
        value /= 100;
      }

      return value;
    }
  }

  return null;
};
